import { useEffect, useState } from "react";
import { API, graphqlOperation } from "aws-amplify";
import axios from "axios";
import {
  IconButton,
  Center,
  CircularProgress,
  Flex,
  Grid,
  Button,
  Icon,
  Text,
  Box,
  Alert,
  AlertTitle,
  AlertDescription,
  Card,
  useToast,
  Skeleton,
  Divider,
} from "@chakra-ui/react";

import { IoIosAlert } from "react-icons/io";
import { FaArrowLeft, FaArrowRight, FaCheck } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import AWSAppSyncClient from "aws-appsync";
import { FaCreditCard } from "react-icons/fa";
import {
  updateBooking,
  updateBookingSecurityItem,
} from "../../../graphql/mutations";
import { DamageWaiverPay } from "../../Mini/damageWaiverPay";
import gql from "graphql-tag";
import { PerStayCardDrawer } from "../Documents/Deposit/perStayCardDrawer";
import { DepositCardDrawer } from "../Documents/Deposit/DepositCardDrawer";
import { getBooking, getSecurityItem } from "../../../graphql/queries";
import { Auth } from "aws-amplify";
import { TruviCardDrawer } from "../Documents/Deposit/TruviCardDrawer";
import { nextIncompleteStep } from "../stepFlow";

// Local dev can point payment calls at a local engine via REACT_APP_PAY_API_URL;
// production builds don't set it and fall back to the live API.
const PAY_API = process.env.REACT_APP_PAY_API_URL || "https://api.linkbase.ca";

const query = gql`
  query GetUsers($id: String!) {
    getUsers(users: $id) {
      users
      stripeId
    }
  }
`;

const runQuery = async (id) => {
  const client = new AWSAppSyncClient({
    url: process.env.REACT_APP_URL,
    region: "us-west-2",
    auth: {
      type: "API_KEY",
      apiKey: process.env.REACT_APP_API_KEY,
    },
    disableOffline: true,
  });

  try {
    const result = await client.query({
      query,
      variables: { id },
    });
    return result.data.getUsers;
  } catch (error) {
    console.log("GraphQL query error:", error);
    return null;
  }
};

const DamageDepositArea = ({ booking, fullSecurityItem, currency, onOpen, optionNumber }) => {
  // The card-validation fee is $5 USD for nearly every account, but a few
  // hosts are on a negotiated rate. Start at $5 so the copy reads correctly
  // before the request resolves and if it never does.
  const [feeUSD, setFeeUSD] = useState(5);

  useEffect(() => {
    if (!booking?.id) return;
    let cancelled = false;

    const fetchValidationFee = async () => {
      try {
        const session = await Auth.currentSession();
        const idToken = session.getIdToken().getJwtToken();

        const response = await axios.post(
          `${PAY_API}/payments/platform-fee`,
          { bookingId: booking.id, currency: "usd" },
          {
            headers: {
              Authorization: `Bearer ${idToken}`,
              "Content-Type": "application/json",
              "x-api-key": process.env.REACT_APP_LINKBASE_API_KEY,
            },
          }
        );

        const usd = response.data?.data?.usdEquivalent;
        if (!cancelled && typeof usd === "number" && usd > 0) {
          setFeeUSD(usd);
        }
      } catch (error) {
        // Keep the $5 default: it is the correct figure for every account
        // that has not negotiated something else.
        console.log("Could not load validation fee, showing default:", error);
      }
    };

    fetchValidationFee();
    return () => {
      cancelled = true;
    };
  }, [booking?.id]);

  // "$5" stays "$5" rather than "$5.00"; a custom rate shows its cents.
  const feeLabel = Number.isInteger(feeUSD) ? `$${feeUSD}` : `$${feeUSD.toFixed(2)}`;

  // Extract the security items
  const depositItem = booking.securityItems.items.find(
    (item) => item.type === "DEPOSIT" && item.hasDeposit === true
  );

  // Only render if a valid DEPOSIT item with hasDeposit set to true exists
  if (!depositItem && !fullSecurityItem) {
    return null;
  }

  return (
    <>
      {optionNumber ? (
        <Text fontWeight={800} fontSize={"16px"} pt={"2px"} mt={4}>
          Option {optionNumber}
        </Text>
      ) : null}
      <Text fontWeight={700} fontSize={"16px"} pt={"2px"}>
        Damage Deposit
      </Text>
      <Text mt={1} fontWeight={600} fontSize={"12px"}>
        Pay a refundable damage deposit ({feeLabel} USD validation fee applies), held until 7 days after checkout.
      </Text>

      <Card
        mt={6}
        p={3}
        w={"full"}
        direction={"row"}
        overflow="hidden"
        variant="outline"
      >
        <Flex justifyContent={"space-between"} w={"100%"}>
          <Flex flexDirection={"column"}>
            <Text fontWeight={800} fontSize={"12px"}>
              Damage Deposit
            </Text>

            {fullSecurityItem && currency ? (
              <Text fontWeight={800} fontSize={"18px"} pt={1}>
                {new Intl.NumberFormat("en-CA", {
                  style: "currency",
                  currency: currency.toUpperCase(),
                }).format(fullSecurityItem.amount)}{" "}
                <Text as="span" fontSize={"12px"}>
                  {currency.toUpperCase()}
                </Text>
              </Text>
            ) : (
              <Box pt={2}>
                <Skeleton w="120px" h="24px" borderRadius="6px" />
              </Box>
            )}
          </Flex>
          <Center>
            <Button
              onClick={onOpen}
              minW={"42%"}
              bg={"#817efc"}
              variant="solid"
              borderRadius="10px"
              fontSize="12px"
              // isLoading={loadingCard}
              leftIcon={<FaCreditCard />}
            >
              Secure With Card
            </Button>
          </Center>
        </Flex>
      </Card>
    </>
  );
};

const PerStayDepositArea = ({ fullSecurityItem, currency, onOpen, optionNumber }) => {
  const [platformFee, setPlatformFee] = useState(null);
  const [platformFeeLoading, setPlatformFeeLoading] = useState(false);
  const [totalAmount, setTotalAmount] = useState(null);

  // Fetch platform fee when component mounts
  useEffect(() => {
    // Only fetch if perStayEnabled is true
    if (!fullSecurityItem?.perStayEnabled) {
      return;
    }

    const fetchPlatformFee = async () => {
      if (!fullSecurityItem?.owner) {
        console.log("No owner found for security item");
        return;
      }
      
      try {
        setPlatformFeeLoading(true);
        
        // Get current session for authentication
        const session = await Auth.currentSession();
        const idToken = session.getIdToken().getJwtToken();

        // First get the owner's stripe account ID
        const ownerResponse = await runQuery(fullSecurityItem.owner);
        const stripeAccountId = ownerResponse?.stripeId;

        if (!stripeAccountId) {
          // Still set a default platform fee for display
          const defaultFee = {
            amount: 7.67,
            amountFormatted: "7.67",
            currency: "cad"
          };
          setPlatformFee(defaultFee);
          setTotalAmount(fullSecurityItem.perStayAmount + defaultFee.amount);
          return;
        }

        // Call platform fee endpoint using direct fetch instead of AWS Amplify API
        const response = await axios.post(`${PAY_API}/payments/platform-fee`, {
          connectedAccountId: stripeAccountId
        }, {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json',
            'x-api-key': process.env.REACT_APP_LINKBASE_API_KEY,
          },
        });

        const responseData = response.data;

        // Check if the API call was successful
        if (responseData?.success) {
          const feeData = responseData.data;
          setPlatformFee(feeData);
          
          // Calculate total amount (perStayAmount + platform fee)
          const baseAmount = fullSecurityItem.perStayAmount;
          const feeAmountInCents = feeData.amount; // This is in cents (e.g., 500 = $5.00)
          const feeAmountInDollars = feeAmountInCents / 100; // Convert cents to dollars
          const total = baseAmount + feeAmountInDollars;
          setTotalAmount(total);
        } else {
          console.warn("Platform fee API did not return success");
          // Set default fee for display
          const defaultFee = {
            amount: 7.67,
            amountFormatted: "7.67",
            currency: "cad"
          };
          setPlatformFee(defaultFee);
          setTotalAmount(fullSecurityItem.perStayAmount + defaultFee.amount);
        }
      } catch (error) {
        console.error("Error fetching platform fee:", error);
        // Set default fee for display even on error
        const defaultFee = {
          amount: 7.67,
          amountFormatted: "7.67",
          currency: "cad"
        };
        setPlatformFee(defaultFee);
        setTotalAmount(fullSecurityItem.perStayAmount + defaultFee.amount);
      } finally {
        setPlatformFeeLoading(false);
      }
    };

    fetchPlatformFee();
  }, [fullSecurityItem]);

  const handleOpen = () => {
    // Pass platform fee data to the parent component
    onOpen({
      platformFee,
      totalAmount: totalAmount || fullSecurityItem.perStayAmount
    });
  };

  // Only render if perStayEnabled is true
  if (!fullSecurityItem?.perStayEnabled) {
    return null;
  }

  return (
    <>
      {optionNumber ? (
        <Text fontWeight={800} fontSize={"16px"} pt={"2px"} mt={4}>
          Option {optionNumber}
        </Text>
      ) : null}
      <Text fontWeight={700} fontSize={"16px"} pt={"2px"}>
        Per Stay Waiver
      </Text>
      <Text mt={1} fontWeight={600} fontSize={"12px"}>
        Pay a flat (non-refundable) rate waiver for your entire stay, regardless of duration.
        This option provides a fixed cost regardless of your length of stay.
      </Text>

      <Card
        mt={6}
        p={3}
        mb={6}
        w={"full"}
        direction={"row"}
        overflow="hidden"
        variant="outline"
      >
        <Flex justifyContent={"space-between"} w={"100%"}>
          <Flex flexDirection={"column"}>
            <Text fontWeight={800} fontSize={"12px"}>
              Per Stay Deposit
            </Text>

            {fullSecurityItem && currency ? (
              <Box>
                {platformFeeLoading ? (
                  <Box pt={2}>
                    <Skeleton w="120px" h="24px" borderRadius="6px" />
                  </Box>
                ) : (
                  <>
                    <Text fontWeight={800} fontSize={"18px"} pt={1}>
                      {new Intl.NumberFormat("en-CA", {
                        style: "currency",
                        currency: currency.toUpperCase(),
                      }).format(totalAmount || fullSecurityItem.perStayAmount)}{" "}
                      <Text as="span" fontSize={"12px"}>
                        {currency.toUpperCase()}
                      </Text>
                    </Text>
                  </>
                )}
              </Box>
            ) : (
              <Box pt={2}>
                <Skeleton w="120px" h="24px" borderRadius="6px" />
              </Box>
            )}
          </Flex>
          <Center>
            <Button
              onClick={handleOpen}
              minW={"42%"}
              bg={"#817efc"}
              variant="solid"
              borderRadius="10px"
              fontSize="12px"
              leftIcon={<FaCreditCard />}
            >
              Pay Per Stay
            </Button>
          </Center>
        </Flex>
      </Card>
    </>
  );
};

const VerificationAlert = ({ status }) => {
  if (!status) return null;

  let alertProps = {};

  if (status === "Approved") {
    alertProps = {
      bg: "green.50",
      color: "green.800",
      iconColor: "green.500",
      title: "You are Approved",
      description: "You are approved for full coverage in the case of damage.",
    };
  } else if (status === "Flagged") {
    alertProps = {
      bg: "yellow.50",
      color: "yellow.800",
      iconColor: "yellow.500",
      title: "Partial Coverage",
      description:
        "You are approved for partial coverage in the case of damage.",
    };
  } else if (status === "Rejected") {
    alertProps = {
      bg: "red.50",
      color: "red.800",
      iconColor: "red.500",
      title: "Verification Rejected",
      description:
        "We regret to inform you that we cannot provide coverage at this time.",
    };
  } else if (status === "integrated_waiver") {
    alertProps = {
      bg: "green.50",
      color: "green.800",
      iconColor: "green.500",
      title: "No action needed",
      description: "There's nothing for you to do for this step.",
    };
  } else if (status === "uncaptured") {
    alertProps = {
      bg: "blue.50",
      color: "blue.800",
      iconColor: "blue.500",
      title: "Deposit Authorized",
      description:
        "Your damage deposit has been authorized and funds are held. They will be released or captured after checkout.",
    };
  }

  return (
    <Alert
      mt={"10px"}
      borderRadius={"10px"}
      bg={alertProps.bg}
      color={alertProps.color}
      display={"flex"}
      alignItems={"center"}
      p={4}
    >
      <Icon
        as={IoIosAlert}
        mr="15px"
        fontSize={"20px"}
        color={alertProps.iconColor}
      />

      <Box>
        <AlertTitle fontSize={"14px"}>{alertProps.title}</AlertTitle>
        <AlertDescription
          fontSize={"12px"}
          display={"block"}
          lineHeight={"18px"}
        >
          {alertProps.description}
        </AlertDescription>
      </Box>
    </Alert>
  );
};

export const Deposit = () => {
  const location = useLocation();
  const { guest, room, booking, storedStripeId, manager, accessTimes } = location.state || {};
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingCard, setLoadingCard] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [isPaymentComplete, setIsPaymentComplete] = useState(true);
  const [stripePromise, setStripePromise] = useState(null);
  const toast = useToast();

  const [totalPrice, setTotalPrice] = useState(null);
  const [stripeId, setStripeId] = useState(storedStripeId || null);

  const [fullSecurityItem, setFullSecurityItem] = useState(null);

  // Freshest booking we've seen (set by fetchLatestBookingStatus) — used by
  // the Continue button to work out which step is still left.
  const [latestBooking, setLatestBooking] = useState(null);

  // Platform fee data for per stay payments
  const [perStayPaymentData, setPerStayPaymentData] = useState(null);

  // Per stay drawer state
  const [perStayDrawerOpen, setPerStayDrawerOpen] = useState(false);
  const [perStayStripeAccountId, setPerStayStripeAccountId] = useState(null);

  // Truvi drawer state. truviAmount + truviCurrency are paired — both
  // come from the same /payments/smart/quote response we re-fetch in
  // the Pay button handler. They must travel together to the drawer
  // so the eventual /payments/smart call can't mix a CAD-cents amount
  // with a USD currency tag (or vice versa).
  const [truviDrawerOpen, setTruviDrawerOpen] = useState(false);
  const [truviStripeAccountId, setTruviStripeAccountId] = useState(null);
  const [truviAmount, setTruviAmount] = useState(null);
  const [truviCurrency, setTruviCurrency] = useState(null);

  // Deposit drawer state
  const [depositDrawerOpen, setDepositDrawerOpen] = useState(false);

  // Truvi quote state
  const [truviQuote, setTruviQuote] = useState(null);
  const [truviQuoteLoading, setTruviQuoteLoading] = useState(false);

  // Options the host allows for this booking's channel, from the engine.
  // null = not loaded / lookup failed → fall back to the security item's
  // own flags (the pre-channel-rules behaviour). The payment endpoints
  // enforce the same rules, so a stale null can't let a guest buy an
  // option the host has excluded.
  const [allowedOptions, setAllowedOptions] = useState(null);

  // Load Stripe with environment variable
  const createStripePromise = async () => {
    try {
      const stripe = await loadStripe(process.env.REACT_APP_STRIPE_API_KEY);
      return stripe;
    } catch (error) {
      console.error("Error loading Stripe:", error);
      throw error;
    }
  };

  useEffect(() => {
    createStripePromise().then(setStripePromise);
  }, []);

  useEffect(() => {
    const fetchStripeIdAndPrice = async () => {
      let activeStripeId = storedStripeId;

      if (!activeStripeId && guest?.owner) {
        const user = await runQuery(guest.owner);
        if (user?.stripeId) {
          activeStripeId = user.stripeId;
          setStripeId(activeStripeId);
        }
      }

      if (booking?.checkInDate && booking?.checkOutDate && activeStripeId) {
        const price = await calculatePrice(
          booking.checkInDate,
          booking.checkOutDate,
          activeStripeId
        );
        setTotalPrice(price);
      }
    };

    fetchStripeIdAndPrice();
  }, [booking, guest, storedStripeId]);

  // Function to calculate price based on check-in and check-out dates
  const calculatePrice = async (checkInDate, checkOutDate, stripeId) => {
    if (checkInDate && checkOutDate) {
      const checkIn = new Date(checkInDate);
      const checkOut = new Date(checkOutDate);
      const diffInTime = checkOut - checkIn;
      const diffInDays = Math.ceil(diffInTime / (1000 * 60 * 60 * 24));

      try {
        const response = await API.post(
          "payStripe",
          "/pay/getConvertedAmount",
          {
            body: {
              stripeId,
            },
          }
        );

        const perDay = response?.amount || 7; // fallback if missing
        const currency = response?.currency || "usd";

        return {
          currency,
          amount: perDay * diffInDays,
          perDay,
          days: diffInDays,
        };
      } catch (error) {
        console.error(
          "Error fetching converted price. Defaulting to USD.",
          error
        );
        return {
          currency: "usd",
          amount: diffInDays * 7,
          perDay: 7,
          days: diffInDays,
        };
      }
    }

    return {
      currency: "usd",
      amount: 0,
      perDay: 7,
      days: 0,
    };
  };

  // ✅ Move fetchLatestBookingStatus outside useEffect so it can be accessed by onPaymentSuccess
  const fetchLatestBookingStatus = async () => {
    try {
      const res = await API.graphql(
        graphqlOperation(getBooking, { id: booking.id })
      );

      const updatedBooking = res.data?.getBooking;
      
      const updatedDepositItem = updatedBooking.securityItems.items.find(
        (item) => item.type === "DEPOSIT"
      );

      if (updatedDepositItem?.status) {
        setVerificationStatus(updatedDepositItem.status);
      }

      if (updatedDepositItem?.isComplete) {
        setIsPaymentComplete(true);
      } else {
        setIsPaymentComplete(false); // ✅ explicitly set to false if not complete
      }

      if (updatedDepositItem?.securityItemID) {
        const securityItemRes = await API.graphql(
          graphqlOperation(getSecurityItem, {
            id: updatedDepositItem.securityItemID,
          })
        );
        const fullItem = securityItemRes.data?.getSecurityItem;
        


        // Set fullSecurityItem if any option is enabled, or if truviEnabled is null/undefined (legacy)
        if (
          fullItem?.truviEnabled === true ||
          fullItem?.truviEnabled == null ||
          fullItem?.hasDeposit ||
          fullItem?.perStayEnabled
        ) {
          setFullSecurityItem(fullItem);
        } else {
          setFullSecurityItem(null);
        }
      } else {
        console.warn("No securityItemID found on DEPOSIT item.");
        setFullSecurityItem(null); // fallback if no ID
      }
      // Keep the freshest security items around so the Continue button can
      // work out what the guest still has left to do.
      const merged = { ...booking, securityItems: updatedBooking.securityItems };
      setLatestBooking(merged);
      return merged;
    } catch (error) {
      console.error("Failed to refresh booking status:", error);
      setIsPaymentComplete(false);
      setFullSecurityItem(null);
      return null;
    }
  };

  // Once this step is done, the guest presses Continue to move on —
  // navigation here is always an explicit button press, never automatic.
  // The state must carry EVERYTHING the step screens and /onboarding read
  // (manager drives extended ID verification; accessTimes feeds the done
  // screen) or a hop through this screen silently degrades the next one.
  const stepNavState = () => ({
    guest,
    room,
    booking: latestBooking || booking,
    storedStripeId,
    manager,
    accessTimes,
  });

  // Back always means the checklist — never history. Continue buttons push
  // step screens onto the history stack, so navigate(-1) would land on the
  // previous (often already-completed) step instead of home. `replace`
  // keeps finished step screens out of the stack entirely, so even the
  // browser's own back button returns to the checklist, not a done step.
  const goToChecklist = () => {
    navigate("/onboarding", {
      state: { ...stepNavState(), needsRefresh: true },
      replace: true,
    });
  };

  const goToNextStep = () => {
    const next = nextIncompleteStep(latestBooking || booking, "DEPOSIT");
    if (next) {
      navigate(next.path, { state: stepNavState(), replace: true });
    } else {
      goToChecklist();
    }
  };

  useEffect(() => {
    fetchLatestBookingStatus();
  }, [booking.id]);

  useEffect(() => {
    if (!booking?.id) return;
    let cancelled = false;
    const fetchAllowedOptions = async () => {
      try {
        const session = await Auth.currentSession();
        const idToken = session.getIdToken().getJwtToken();
        const response = await axios.get(
          `${PAY_API}/security/booking/${booking.id}/protection-options`,
          {
            headers: {
              'Authorization': `Bearer ${idToken}`,
              'x-api-key': process.env.REACT_APP_LINKBASE_API_KEY,
            },
          }
        );
        if (!cancelled && response.data?.success && Array.isArray(response.data.data?.options)) {
          setAllowedOptions(response.data.data.options);
        }
      } catch (error) {
        console.warn("Could not load protection options; showing all configured options.", error);
      }
    };
    fetchAllowedOptions();
    return () => {
      cancelled = true;
    };
  }, [booking?.id]);

  // The options this guest actually sees, in display order: the host's
  // flags on the security item, narrowed by the channel rules when loaded.
  const isAllowed = (option) => !allowedOptions || allowedOptions.includes(option);
  const shownOptions = [
    fullSecurityItem &&
      (fullSecurityItem.truviEnabled === true || fullSecurityItem.truviEnabled == null) &&
      isAllowed("truvi_waiver") &&
      "truvi_waiver",
    fullSecurityItem?.hasDeposit && isAllowed("deposit") && "deposit",
    fullSecurityItem?.perStayEnabled && isAllowed("host_waiver") && "host_waiver",
  ].filter(Boolean);
  // A single option is simply what's required — no "Option 1" heading.
  const optionNumberOf = (option) =>
    shownOptions.length > 1 ? shownOptions.indexOf(option) + 1 : null;

  // Fetch Truvi quote on mount or when booking.id changes
  useEffect(() => {
    const fetchTruviQuote = async () => {
      if (!booking?.id) return;
      setTruviQuoteLoading(true);
      try {
        const session = await Auth.currentSession();
        const idToken = session.getIdToken().getJwtToken();
        const response = await axios.post(
          `${PAY_API}/payments/smart/quote`,
          { bookingId: booking.id },
          {
            headers: {
              'Authorization': `Bearer ${idToken}`,
              'Content-Type': 'application/json',
              'x-api-key': process.env.REACT_APP_LINKBASE_API_KEY,
            },
          }
        );

        if (response.data?.success) {
          setTruviQuote(response.data.data);
        } else {
          setTruviQuote(null);
        }
      } catch (error) {
        setTruviQuote(null);
      } finally {
        setTruviQuoteLoading(false);
      }
    };
    fetchTruviQuote();
  }, [booking?.id]);

  const handleWaiverCall = async () => {
    setLoading(true);

    try {
      const nameParts = guest.name.trim().split(/\s+/);

      const guestDetails = {
        firstName: nameParts[0] || "Guest",
        lastName: nameParts.slice(1).join(" ") || "Name",
        email: guest.email,
        telephonenumber: guest.phone,
      };

      const verificationTypes = ["fraudDetection", "watchlist"];

      const payload = {
        guestDetails,
        verificationTypes,
        room: {
          address: JSON.parse(room.address),
          listingId: room.id,
          listingName: room.name,
          petsAllowed: "True",
        },
        reservation: {
          checkIn: booking.checkInDate,
          checkOut: booking.checkOutDate,
          reservationId: booking.id,
          creationDate: booking.createdAt,
          source: booking.source,
        },
      };

      const res = await API.post("payStripe", "/pay/superhogWaiver", {
        body: payload,
      });



      const verificationStatus = res.data?.verification?.status || "Unknown";
      const verificationId = res.data?.verification?.verificationId;

      try {
        const depositSecurityItem = booking.securityItems.items.find(
          (item) => item.type === "DEPOSIT"
        );



        if (depositSecurityItem) {
          const bookingSecurityItemUpdateInput = {
            id: depositSecurityItem.id,
            status: verificationStatus,
            isComplete: true,
            verificationId: verificationId,
          };

          const securityItemRes = await API.graphql(
            graphqlOperation(updateBookingSecurityItem, {
              input: bookingSecurityItemUpdateInput,
            })
          );



          // ✅ Update local state to reflect successful payment
          setVerificationStatus(verificationStatus);
          setIsPaymentComplete(true);
        } else {
          console.warn("No DEPOSIT security item found for this booking.");
        }
      } catch (securityUpdateError) {
        console.error("Error updating security item:", securityUpdateError);
      }

      setVerificationStatus(verificationStatus);

      toast({
        title: "Success",
        description: "Verification request completed successfully.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });

      return true;
    } catch (error) {
      console.error("Error calling API:", error);

      toast({
        title: "Error",
        description: "Failed to complete verification request.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });

      return false;
    } finally {
      setLoading(false);
    }
  };


  return (
    <>
      {stripePromise && (
        <Elements stripe={stripePromise}>
          <Box p={{ base: 4, md: 8 }} maxW="640px" mx="auto">
            <Flex alignItems="center" mb={4}>
              <IconButton
                icon={<Icon as={FaArrowLeft} />}
                variant="ghost"
                colorScheme="blue"
                bg="#2c3b4b"
                mr={2}
                onClick={goToChecklist}
                aria-label="Back"
              />

              <Text fontWeight="bold" fontSize="14px">
                Damage Deposit
              </Text>
            </Flex>
            <Flex
              h={"65vh"}
              justifyContent={"flex-start"}
              flexDirection="column"
              mt={"40px"}
            >
              <Center flexDirection="column" alignItems={"start"}>
                {!isPaymentComplete && (
                  <>
                    {/* Option 1: Smart Damage Waiver */}
                    {shownOptions.includes("truvi_waiver") && (
                      <>
                        {optionNumberOf("truvi_waiver") ? (
                          <Text fontWeight={800} fontSize={"16px"} pt={"2px"}>
                            Option {optionNumberOf("truvi_waiver")}
                          </Text>
                        ) : null}
                        <Text fontWeight={700} fontSize={"16px"} pt={"2px"}>
                          Smart Damage Waiver
                        </Text>
                        <Text mt={1} fontWeight={600} fontSize={"12px"}>
                          Pay a small (Non-Refundable) waiver fee for basic damage
                          coverage. It doesn't cover all damages, please maintain the
                          property.
                        </Text>
                        {/* Truvi Price Display */}
                        <Box mt={2} mb={1}>
                          {truviQuoteLoading ? (
                            <Skeleton w="120px" h="24px" borderRadius="6px" />
                          ) : truviQuote ? (
                            <Text fontWeight={800} fontSize={"18px"} pt={1}>
                              {new Intl.NumberFormat("en-CA", {
                                style: "currency",
                                currency: truviQuote.currency?.toUpperCase() || "CAD",
                              }).format(truviQuote.totalAmount / 100)}{' '}
                              <Text as="span" fontSize={"12px"}>
                                {truviQuote.currency?.toUpperCase()}
                              </Text>
                            </Text>
                          ) : null}
                        </Box>
                        <Button
                          onClick={async () => {
                            // Get the stripe account ID for Truvi payments
                            const ownerResponse = await runQuery(guest.owner);
                            const stripeAccountId = ownerResponse?.stripeId;
                            if (!stripeAccountId || !truviQuote) {
                              toast({
                                title: "Error",
                                description: "Could not find connected account for Truvi payment.",
                                status: "error",
                                duration: 4000,
                                isClosable: true,
                              });
                              return;
                            }

                            // Re-quote immediately before charging. The
                            // on-mount quote is held in component state
                            // for the entire lifetime of this screen and
                            // can go stale if the host updates the
                            // SecurityItem.minimumPayment between page
                            // render and Pay tap. The /payments/smart
                            // endpoint trusts whatever amount the FE
                            // sends, so we re-quote here to make sure
                            // the cents value we pass through is
                            // reflective of current host config — not
                            // a value that may be minutes or hours old.
                            let freshQuote;
                            try {
                              const session = await Auth.currentSession();
                              const idToken = session.getIdToken().getJwtToken();
                              const requoteResponse = await axios.post(
                                `${PAY_API}/payments/smart/quote`,
                                { bookingId: booking.id },
                                {
                                  headers: {
                                    'Authorization': `Bearer ${idToken}`,
                                    'Content-Type': 'application/json',
                                    'x-api-key': process.env.REACT_APP_LINKBASE_API_KEY,
                                  },
                                }
                              );

                              if (!requoteResponse.data?.success || !requoteResponse.data?.data) {
                                throw new Error(requoteResponse.data?.error || 'Quote unavailable');
                              }
                              freshQuote = requoteResponse.data.data;
                            } catch (err) {
                              console.error('Truvi re-quote failed:', err);
                              toast({
                                title: "Couldn't refresh price",
                                description: "Please try again in a moment.",
                                status: "error",
                                duration: 4000,
                                isClosable: true,
                              });
                              return;
                            }

                            setTruviStripeAccountId(stripeAccountId);
                            setTruviAmount(freshQuote.totalAmount);
                            setTruviCurrency(freshQuote.currency);
                            setTruviDrawerOpen(true);
                          }}
                          w="full"
                          bg={"#817efc"}
                          variant="solid"
                          borderRadius="10px"
                          fontSize="12px"
                          leftIcon={<FaCreditCard />}
                          mt={2}
                        >
                          Pay With Card
                        </Button>
                      </>
                    )}

                    {shownOptions.includes("truvi_waiver") && shownOptions.length > 1 && <Divider mt={6} />}
                    {shownOptions.includes("deposit") && (
                      <DamageDepositArea
                        booking={booking}
                        fullSecurityItem={fullSecurityItem}
                        currency={totalPrice?.currency}
                        onOpen={() => {
                          setDepositDrawerOpen(true);
                        }}
                        optionNumber={optionNumberOf("deposit")}
                      />
                    )}
                    {shownOptions.includes("host_waiver") && (
                      <>
                        {shownOptions.includes("deposit") && <Divider mt={6} />}
                        <PerStayDepositArea
                          fullSecurityItem={fullSecurityItem}
                          currency={totalPrice?.currency}
                          onOpen={async (data) => {
                            // Get the stripe account ID for per stay payments
                            const ownerResponse = await runQuery(fullSecurityItem.owner);
                            const stripeAccountId = ownerResponse?.stripeId;
                            
                            if (stripeAccountId) {
                              setPerStayStripeAccountId(stripeAccountId);
                              setPerStayPaymentData(data);
                              setPerStayDrawerOpen(true);
                            } else {
                              toast({
                                title: "Error",
                                description: "Could not find connected account for per stay payment.",
                                status: "error",
                                duration: 4000,
                                isClosable: true,
                              });
                            }
                          }}
                          optionNumber={optionNumberOf("host_waiver")}
                        />
                      </>
                    )}
                  </>
                )}

                {/* ✅ Correctly updates VerificationAlert */}
                {verificationStatus && (
                  <VerificationAlert status={verificationStatus} />
                )}

                {/* Step settled → the guest presses Continue to move on. */}
                {isPaymentComplete && verificationStatus &&
                  (() => {
                    const next = nextIncompleteStep(latestBooking || booking, "DEPOSIT");
                    return (
                      <Button
                        onClick={goToNextStep}
                        mt={6}
                        w="full"
                        h="52px"
                        bg="#817efc"
                        color="white"
                        fontSize="15px"
                        fontWeight={700}
                        borderRadius="12px"
                        _hover={{ bg: "#6e6be6" }}
                        _active={{ bg: "#5c59d4" }}
                        leftIcon={next ? undefined : <Icon as={FaArrowLeft} />}
                        rightIcon={next ? <Icon as={FaArrowRight} /> : undefined}
                      >
                        {next ? `Continue to ${next.label}` : "Back to Checklist"}
                      </Button>
                    );
                  })()}
              </Center>
            </Flex>
          </Box>
        </Elements>
      )}
      
      {/* Per Stay Card Drawer - Separate from main Stripe Elements */}
      <PerStayCardDrawer
        isOpen={perStayDrawerOpen}
        onClose={() => {
          setPerStayDrawerOpen(false);
          setPerStayStripeAccountId(null);
          setPerStayPaymentData(null);
        }}
        stripeAccountId={perStayStripeAccountId}
        amount={fullSecurityItem?.perStayAmount ? (fullSecurityItem.perStayAmount * 100) : 0}
        currency={totalPrice?.currency}
        description={`Per Stay Deposit for Booking ${booking?.id}`}
        guest={guest}
        booking={booking}
        fullSecurityItem={fullSecurityItem}
        totalPrice={totalPrice}
        onPaymentSuccess={async (paymentIntentId) => {
          try {
            // Update local state to reflect successful payment
            setVerificationStatus("captured");
            setIsPaymentComplete(true);

            toast({
              title: "Per Stay Waiver Successful",
              description: "Payment has been processed and captured.",
              status: "success",
              duration: 4000,
              isClosable: true,
            });

            await fetchLatestBookingStatus();
          } catch (error) {
            console.error("Error updating local state after per stay payment:", error);
          }
        }}
      />
      {/* Truvi Card Drawer - Smart Damage Waiver */}
      <TruviCardDrawer
        isOpen={truviDrawerOpen}
        onClose={() => {
          setTruviDrawerOpen(false);
          setTruviStripeAccountId(null);
          setTruviAmount(null);
          setTruviCurrency(null);
        }}
        stripeAccountId={truviStripeAccountId}
        amount={truviAmount}
        currency={truviCurrency}
        description={`Smart Damage Waiver for Booking ${booking?.id}`}
        guest={guest}
        booking={booking}
        onPaymentSuccess={async (paymentIntentId) => {
          try {
            // Refresh the booking status to get the updated verification status
            await fetchLatestBookingStatus();
          } catch (error) {
            console.error("Error updating local state after Truvi payment:", error);
          }
        }}
      />
      {/* Deposit Card Drawer - Damage Deposit */}
      <DepositCardDrawer
        isOpen={depositDrawerOpen}
        onClose={() => {
          setDepositDrawerOpen(false);
        }}
        stripeAccountId={stripeId}
        fullSecurityItem={fullSecurityItem}
        totalPrice={totalPrice}
        booking={booking}
        onPaymentSuccess={async (paymentIntentId) => {
          try {
            // Refresh the booking status to get the updated verification status
            await fetchLatestBookingStatus();
          } catch (error) {
            console.error("Error updating local state after deposit payment:", error);
          }
        }}
      />
    </>
  );
};
